interface PaginationProps {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  itemsPerPage: number;
  setItemsPerPage: (perPage: number) => void;
  list: any[];
}

const Pagination = ({
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  list
}: PaginationProps) => {
  // Lapozás kezelése
  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  // Oldalankénti elemszám módosítása
  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Visszaállítjuk az első oldalra
  };

  // Lapozó gombok generálása
  const renderPaginationButtons = () => {
    const totalPages = Math.ceil(list.length / itemsPerPage);
    const buttons = [];

    // Ha nincs oldal, ne jelenítsen meg semmit
    if (totalPages === 0) return null;

    // Mindig mutatjuk az első oldalt
    buttons.push(
      <button
        key={1}
        onClick={() => paginate(1)}
        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'text-gray-500 hover:bg-gray-50'
          }`}
      >
        1
      </button>
    );

    // Ha sok oldal van, akkor "..." jelzést teszünk közbe
    if (totalPages > 7) {
      if (currentPage > 3) {
        buttons.push(
          <span key="dots1" className="px-4 py-2 border border-gray-300 bg-white text-gray-700">
            ...
          </span>
        );
      }

      // Aktuális oldal körüli oldalak megjelenítése
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(currentPage + 1, totalPages - 1);

      for (let i = startPage; i <= endPage; i++) {
        buttons.push(
          <button
            key={i}
            onClick={() => paginate(i)}
            className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === i ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'text-gray-500 hover:bg-gray-50'
              }`}
          >
            {i}
          </button>
        );
      }

      if (currentPage < totalPages - 2) {
        buttons.push(
          <span key="dots2" className="px-4 py-2 border border-gray-300 bg-white text-gray-700">
            ...
          </span>
        );
      }
    } else {
      // Ha kevés oldal van, akkor mindet megjelenítjük
      for (let i = 2; i <= totalPages; i++) {
        buttons.push(
          <button
            key={i}
            onClick={() => paginate(i)}
            className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === i ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'text-gray-500 hover:bg-gray-50'
              }`}
          >
            {i}
          </button>
        );
      }
    }

    return buttons;
  };

  return <div className="px-6 py-4 border-t border-gray-200">
    <div className="flex items-center justify-between">
      <div className="flex-1 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <p className="text-sm text-gray-700">
            {list.length > 0 ? (
              <>
                Mutatva: <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>-
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, list.length)}
                </span> / <span className="font-medium">{list.length}</span>
              </>
            ) : (
              'Nincsenek foglalások'
            )}
          </p>
          <div className="flex items-center gap-2">
            <label htmlFor="itemsPerPage" className="text-sm text-gray-600">
              Oldalankénti elemszám:
            </label>
            <select
              id="itemsPerPage"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="border border-gray-300 rounded-md text-sm py-1 px-2"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
              <option value="25">25</option>
            </select>
          </div>
        </div>
        {list.length > 0 && (
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
              >
                <span className="sr-only">Előző</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>

              {renderPaginationButtons()}

              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === Math.ceil(list.length / itemsPerPage)}
                className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === Math.ceil(list.length / itemsPerPage)
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-500 hover:bg-gray-50'
                  }`}
              >
                <span className="sr-only">Következő</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        )}
      </div>
    </div>
  </div>;
};

export default Pagination;

